import { useComputed } from "@suse-o11y/signal";
import { type Dispatch, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { isShallowEqual } from "~/tools/is-shallow-equal";
import { useHandler } from "~/tools/use-handler";

import { selectAllSearchParams } from "../../../app/state/route/RouteSelectors";
import type { DashboardVariable } from "../../../common/dashboard-spec";
import { useNavigateFrom } from "../../../components/react/link";

import type { DashboardState } from "./_dashboard-state";
import { DashboardVariablesHelpers } from "./_variables";

/**
 * The hook to manage dashboard variables synchronization between form state and URL parameters.
 *
 * @param state - The dashboard form state containing the variables and settings.
 * @returns A tuple containing the current variables and a handler to update them.
 */
export function useDashboardVariables(
	state: DashboardState,
): [ReadonlyArray<DashboardVariable>, Dispatch<ReadonlyArray<DashboardVariable>>] {
	const navigateFrom = useNavigateFrom();
	const { dashboardVariables } = useSelector(selectAllSearchParams);

	const isSaved = useComputed(
		(monitor) => state.form.fields.settings.fields.saveVariables.getOutput(monitor) ?? false,
		[state],
	);

	const variablesFromState = useComputed((monitor) => state.form.fields.variables.getInput(monitor), [state], {
		equals: isShallowEqual,
	});

	const variablesFromUrl = useMemo(
		() => DashboardVariablesHelpers.applyURLValuesToVariables(dashboardVariables ?? [], variablesFromState),
		[dashboardVariables, variablesFromState],
	);

	/**
	 * Split the handler function so it depends only on `isSaved` value, but not on variables arrays.
	 * This way it runs the effect only when `isSaved` changes and applies the latest variables values.
	 */
	const syncVariablesBetweenStateAndUrl = useHandler((saved: boolean) => {
		/**
		 * Sync variables from URL to state when switching to "save variables in dashboard" mode
		 */
		if (saved && variablesFromUrl.length > 0) {
			/*
				The synced value is now the initial, so the user has a starting point for the default value
				which enables the variable field reset to work more intuitively
			*/
			state.form.fields.variables.reset(variablesFromUrl);

			navigateFrom("*", {
				method: "replace",
				search: (params) => ({
					...params,
					dashboardVariables: undefined,
				}),
			});
		}

		/**
		 * Sync variables from state to URL when switching to "save variables in URL" mode
		 */
		if (!saved && variablesFromState.length > 0) {
			navigateFrom("*", {
				method: "replace",
				search: (params) => ({
					...params,
					dashboardVariables: DashboardVariablesHelpers.toURLParams(variablesFromState),
				}),
			});
		}
	});

	useEffect(() => {
		syncVariablesBetweenStateAndUrl(isSaved);
	}, [syncVariablesBetweenStateAndUrl, isSaved]);

	return [
		isSaved ? variablesFromState : variablesFromUrl,

		useHandler((variables) => {
			if (isSaved) {
				state.form.fields.variables.setInput(variables);
			} else {
				navigateFrom("*", {
					method: "replace",
					search: (params) => ({
						...params,
						dashboardVariables: DashboardVariablesHelpers.toURLParams(variables),
					}),
				});
			}
		}),
	];
}
